import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Check, X, Minus, Filter, Download } from "lucide-react";
import { toast } from "sonner";

export default function ProductCoverage() {
  const [payload, setPayload] = useState({ summary: [], rows: [] });
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [agents, setAgents] = useState([]);
  const [f, setF] = useState({ category_id: "", model_id: "", size: "", state: "", agent_id: "", status: "" });

  useEffect(() => {
    api.get("/product-categories").then(r => setCategories(r.data));
    api.get("/products").then(r => setModels(r.data));
    api.get("/agents").then(r => setAgents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/product-coverage", { params }).then(r => setPayload(r.data));
  }, [f]);

  const filteredModels = f.category_id ? models.filter(m => m.category_id === f.category_id) : models;
  const availableSizes = f.model_id ? (models.find(m => m.id === f.model_id)?.available_sizes || []) : [];
  const selectedModel = models.find(m => m.id === f.model_id);

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/reports/product_coverage`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `MLILY-Product-Coverage.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const Icon = ({ status }) => status === "displayed" ? <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} /> :
    status === "not_displayed" ? <X className="w-5 h-5 text-red-600" strokeWidth={3} /> :
    <Minus className="w-5 h-5 text-slate-300" />;

  return (
    <div className="space-y-4" data-testid="product-coverage-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Product Coverage</h1>
          <p className="text-slate-600 mt-1">Model-level coverage across your stores — a store counts once per model when at least one floor unit is displayed.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => doExport("xlsx")} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-coverage-xlsx"><Download className="w-4 h-4" /> Excel</button>
          <button onClick={() => doExport("pdf")} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-coverage-pdf"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap gap-2 items-center">
        <Filter className="w-4 h-4 text-slate-400" />
        <select value={f.category_id} onChange={e => setF({ ...f, category_id: e.target.value, model_id: "", size: "" })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-category">
          <option value="">All categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={f.model_id} onChange={e => setF({ ...f, model_id: e.target.value, size: "" })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-model">
          <option value="">All models</option>
          {filteredModels.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select value={f.size} onChange={e => setF({ ...f, size: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-size" disabled={!availableSizes.length}>
          <option value="">All sizes</option>
          {availableSizes.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={f.state} onChange={e => setF({ ...f, state: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-state">
          <option value="">All states</option>
          {["NSW","VIC","QLD","SA","WA","TAS","NT","ACT"].map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={f.agent_id} onChange={e => setF({ ...f, agent_id: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-agent">
          <option value="">All agents</option>
          {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={f.status} onChange={e => setF({ ...f, status: e.target.value })} className="h-10 px-3 border rounded-lg text-sm" data-testid="filter-status">
          <option value="">Any status</option>
          <option value="displayed">✓ Displayed</option>
          <option value="not_displayed">✕ Not displayed</option>
          <option value="unchecked">— Not checked</option>
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {payload.summary.map(s => (
          <div key={s.model_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`summary-${s.model_id}`}>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{s.category_name}</div>
            <div className="font-heading font-bold text-[#001F54] mt-0.5">{s.model_name}</div>
            <div className="text-3xl font-heading font-black text-[#003DA5] mt-2">{s.coverage_pct}%</div>
            <div className="text-xs text-slate-500 mt-1">{s.displayed}/{s.total} stores displaying</div>
            <div className="flex gap-3 text-xs mt-2">
              <span className="text-emerald-600">✓ {s.displayed}</span>
              <span className="text-red-600">✕ {s.not_displayed}</span>
              <span className="text-slate-400">— {s.unchecked}</span>
            </div>
          </div>
        ))}
        {payload.summary.length === 0 && <div className="col-span-full text-center text-slate-500 py-4">No models match filters.</div>}
      </div>

      {/* Detail rows */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <div className="font-heading font-semibold text-[#001F54]">{selectedModel ? `${selectedModel.name} — Floor stock detail` : "Floor stock detail"} ({payload.rows.length} rows)</div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F7FC] text-xs uppercase text-slate-500 sticky top-0">
              <tr>
                <th className="text-left px-4 py-3">Store</th>
                <th className="text-left px-4 py-3">Group</th>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3">Model</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Sizes displayed</th>
                <th className="text-left px-4 py-3">Units</th>
                <th className="text-left px-4 py-3">Verified</th>
              </tr>
            </thead>
            <tbody>
              {payload.rows.slice(0, 500).map((r, i) => (
                <tr key={i} className="border-t hover:bg-slate-50" data-testid={`coverage-row-${r.customer_id}-${r.model_id}`}>
                  <td className="px-4 py-2"><Link to={`/accounts/${r.customer_id}`} className="text-[#003DA5] hover:underline font-medium" data-testid={`coverage-store-link-${r.customer_id}`}>{r.customer_name}</Link></td>
                  <td className="px-4 py-2 text-xs text-slate-500">{r.customer_group || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.state}</td>
                  <td className="px-4 py-2 text-xs">{r.category_name} / <b>{r.model_name}</b></td>
                  <td className="px-4 py-2"><Icon status={r.status} /></td>
                  <td className="px-4 py-2 text-xs">{r.sizes_present.join(", ") || "—"}</td>
                  <td className="px-4 py-2 text-xs">{r.unit_count}</td>
                  <td className="px-4 py-2 text-[10px] text-slate-500">{r.last_verified_at ? new Date(r.last_verified_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {payload.rows.length === 0 && <tr><td colSpan="8" className="px-4 py-12 text-center text-slate-500">No coverage data yet — record floor visits.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex flex-wrap gap-4">
        <span className="inline-flex items-center gap-1"><Check className="w-4 h-4 text-emerald-600" strokeWidth={3} /> Displayed (at least one floor unit)</span>
        <span className="inline-flex items-center gap-1"><X className="w-4 h-4 text-red-600" strokeWidth={3} /> Not displayed</span>
        <span className="inline-flex items-center gap-1"><Minus className="w-4 h-4 text-slate-300" /> Not yet checked</span>
      </div>
    </div>
  );
}
